<template>
	<BurnUpChartBase
		class="burn-up-chart-time__chart"
		data-testid="burn-up-chart-time"
		:project-id="projectId"
		:retainer-period-id="retainerPeriodId"
		:loading="loading"
		:project="project"
		:make-y-axis-duration-options="yAxisDuration"
		:is-viewed-in-grid="isViewedInGrid"
		:has-sidebar="false"
		:budget-plot-lines="timeBudgetPlotLines"
		:plot-lines="plotLines"
		:gradient-lines="gradientLines"
		:tooltip-options="tooltipOptions"
		:progress="progress"
		:max-axis-value="maxAxisValue"
		:seconds-per-tick="secondsPerTick"
	/>
</template>
<script lang="ts" setup>
import { computed, reactive, toRefs, watch } from 'vue';
import BurnUpChartBase from './BurnUpChartBase.vue';
import {
	type BurnUpChartQueryVariables,
	type Project,
	ProjectProgressGroupBy,
	type RetainerPeriod,
	useBurnUpChartQuery,
} from '@/generated/graphql';
import {
	type TooltipLine,
	tooltipShared,
	yAxisDuration,
} from '@/lib/highchartsUtils';
import { useRefetchOnSocketMessage } from '@/lib/apollo/apolloTools';
import { useFormatter, useTranslation } from '@/composables';
import {
	APOLLO_PARAMS_PERSISTED_QUERY,
	usePersistedQueryLoading,
} from '@/lib/apollo/apollo';
import {
	MARKER_RADIUS,
	BurnUpChartLineNameTranslations,
	DAY_INTERVAL_MS,
} from '../constants';
import {
	type ChartProgressGroup,
	type ChartProject,
	ChartZoneClasses,
} from '../types';
import { roundDurationToHours } from '@/utils/time';
import {
	getEndMarkerTime,
	getForecastedStartTime,
	getFormattedProgressData,
	getIsTodayAfterProgress,
	getIsTodayBeforeProgress,
	getStartMarkerTime,
	getTodaysDateIndex,
	getYValues,
} from '../helpers';
import { SOCKET_MESSAGES_THAT_TRIGGER_REFETCHING } from '@/features/Project/constants';

const props = defineProps<{
	projectId: Project['id'];
	retainerPeriodId?: RetainerPeriod['id'];
	isViewedInGrid?: boolean;
}>();

const { projectId, retainerPeriodId } = toRefs(props);

const { t } = useTranslation();

const variables = reactive<BurnUpChartQueryVariables>({
	projectInput: {
		projectId: Number(projectId.value),
	},
	projectProgressInput: {
		groupBy: ProjectProgressGroupBy.Days,
		retainerPeriodId: retainerPeriodId.value,
	},
});

const { result, loading } = useRefetchOnSocketMessage(
	usePersistedQueryLoading(
		useBurnUpChartQuery(variables, APOLLO_PARAMS_PERSISTED_QUERY)
	),
	{
		messages: SOCKET_MESSAGES_THAT_TRIGGER_REFETCHING,
		filter: (message) =>
			String(message.data.project_id) === props.projectId,
	}
);

watch(retainerPeriodId, (retainerPeriodId) => {
	variables.projectProgressInput.retainerPeriodId = retainerPeriodId;
});

const project = computed<ChartProject | undefined>(() => {
	if (result?.value?.Project) {
		return {
			...result.value.Project,
			progressData: (result.value.Project.progressData || []).map(
				(elem): ChartProgressGroup => ({
					budgetValue: elem.timeBudget,
					groupName: elem.groupName,
					plannedValue: elem.plannedTime,
					actualValue: elem.actualTime,
					forecastedValue: elem.forecastedTime,
					actualCost: 0,
					actualRevenue: 0,
					bookingsCost: 0,
					costBudget: 0,
					forecastedCost: 0,
					forecastedRevenue: 0,
					monetaryBudget: 0,
					tasksCost: 0,
				})
			),
		};
	}

	return undefined;
});

const { formatLongDuration, formatDateShortNoCurrentYear } = useFormatter();

const makeTooltipFormatterTime =
	(): ((duration: number) => string) => (duration: number) => {
		return formatLongDuration({
			duration:
				duration >= 3600 ? roundDurationToHours(duration) : duration,
			removeMinutesOnFullHours: true,
			minutesUnitExtraShort: true,
			padMinutesIfNoHours: false,
		});
	};
const progress = computed(() => getFormattedProgressData(project.value));

const isTodayBeforeProgress = computed(() =>
	getIsTodayBeforeProgress(progress.value)
);
const todaysDateIndex = computed(() => getTodaysDateIndex(progress.value));
const isTodayAfterProgress = computed(() =>
	getIsTodayAfterProgress(progress.value)
);
const forecastedStartTime = computed(() =>
	getForecastedStartTime(progress.value)
);
const startMarkerTime = computed(() =>
	getStartMarkerTime(project.value, progress.value, props.retainerPeriodId)
);
const endMarkerTime = computed(() =>
	getEndMarkerTime(project.value, progress.value, props.retainerPeriodId)
);

const budget = computed(() => {
	if (progress.value) {
		return progress.value.at(1)?.budgetValue;
	}
	return undefined;
});
const isBudgetSet = computed(() => !!budget.value);

const timeBudgetPlotLines = computed(() => {
	return [
		...(isBudgetSet.value
			? [
					{
						id: BurnUpChartLineNameTranslations.Budget,
						value: budget.value,
						zIndex: 5,
						label: {
							y: -8,
							x: 15,
							formatter() {
								return `<div style="color: #00848E; font-weight: 600" >${t(
									'project_burn_up_chart_budget'
								)}</div>`;
							},
						},
						className: 'budget-line-time',
					},
				]
			: []),
	] as Highcharts.YAxisPlotLinesOptions[];
});

const plannedValues = computed(() => {
	let sum = 0;
	return progress.value.map((progressGroup) => {
		sum += progressGroup.plannedValue;
		return sum;
	});
});

const forecastedValues = computed(() => {
	if (isTodayAfterProgress.value) {
		return [];
	}

	const endIndexActual = isTodayBeforeProgress.value
		? 0
		: todaysDateIndex.value + 1;
	const startIndexForecasted = isTodayBeforeProgress.value
		? 0
		: todaysDateIndex.value;

	let sum = progress.value
		.slice(0, endIndexActual)
		.reduce((acc, curr) => acc + curr.actualValue, 0);

	return progress.value
		.slice(startIndexForecasted)
		.map((progressGroup, i) => {
			sum += progressGroup.forecastedValue;
			return {
				x: forecastedStartTime.value + i * DAY_INTERVAL_MS,
				y: sum,
			};
		});
});

const actualValues = computed(() => {
	if (isTodayBeforeProgress.value) {
		return [];
	}

	let sum = 0;
	return progress.value
		.slice(0, todaysDateIndex.value ? todaysDateIndex.value + 1 : undefined)
		.map((progressGroup, index) => {
			if (index === todaysDateIndex.value) {
				sum += progressGroup.forecastedValue;
			}
			sum += progressGroup.actualValue;
			return sum;
		});
});

const maxAxisValue = computed(() => {
	const minAxisSize = 20 * 3600;

	return Math.max(
		minAxisSize,
		budget.value ?? 0,
		...actualValues.value,
		...plannedValues.value,
		...getYValues(forecastedValues.value)
	);
});

const secondsPerTick = computed<number>(() => {
	const hours = maxAxisValue.value < 100 * 3600 ? 20 : 50;
	return hours * 3600;
});

const budgetData = computed(() => {
	const array = [];
	for (let i = 0; i < progress.value.length; i++) {
		array.push(budget.value);
	}
	return array;
});

const plotLines = computed(() => {
	return [
		{
			name: BurnUpChartLineNameTranslations.Budget,
			data: isBudgetSet.value ? budgetData.value : [],
			type: 'spline',
			showInLegend: isBudgetSet.value,
			legendSymbol: 'lineMarker',
			zIndex: 3,
			marker: {
				symbol: 'circle',
				radius: MARKER_RADIUS,
			},
			states: {
				hover: {
					halo: null,
				},
			},
		},

		{
			name: BurnUpChartLineNameTranslations.Actual,
			type: 'spline',
			data: actualValues.value,
			zones: budget.value
				? [
						{ value: budget.value },
						{ className: ChartZoneClasses.OverActual },
					]
				: undefined,
			zoneAxis: 'y',
			zIndex: 5,
			marker: {
				symbol: 'circle',
				radius: MARKER_RADIUS,
			},
			states: {
				hover: {
					halo: null,
				},
			},
			legendSymbol: 'lineMarker',
		},

		{
			name: BurnUpChartLineNameTranslations.Forecasted,
			type: 'spline',
			data: forecastedValues.value,
			zones: budget.value
				? [
						{ value: budget.value },
						{ className: ChartZoneClasses.OverActual },
					]
				: undefined,
			zoneAxis: 'y',
			zIndex: 5,
			marker: {
				symbol: 'circle',
				radius: MARKER_RADIUS,
			},
			states: {
				hover: {
					halo: null,
				},
			},
			legendSymbol: 'lineMarker',
		},

		{
			name: 'project_timeframe',
			showInLegend: false,
			type: 'spline',
			zIndex: 10,
			data: [
				startMarkerTime.value
					? {
							x: startMarkerTime.value,
							y: 0,
							marker: {
								enabled: true,
								symbol: 'projectStart',
								states: {
									hover: {
										enabled: false,
									},
								},
							},
						}
					: undefined,
				endMarkerTime.value
					? {
							x: endMarkerTime.value,
							y: 0,
							marker: {
								enabled: true,
								symbol: 'projectEnd',
								states: {
									hover: {
										enabled: false,
									},
								},
							},
						}
					: undefined,
			].filter(Boolean),
			states: {
				hover: {
					halo: null,
				},
			},
		},
		{
			name: BurnUpChartLineNameTranslations.Planned,
			type: 'spline',
			data: plannedValues.value,
			zones: budget.value
				? [
						{ value: budget.value },
						{
							className: ChartZoneClasses.OverPlanned,
						},
					]
				: undefined,
			zoneAxis: 'y',
			zIndex: 5,
			marker: {
				symbol: 'circle',
				radius: MARKER_RADIUS,
			},
			states: {
				hover: {
					halo: null,
				},
			},
			legendSymbol: 'lineMarker',
		},
		{
			className: 'project_burn_up_budget_shadow',
			showInLegend: false,
			name: 'project_burn_up_budget_shadow',
			type: 'area',
			zIndex: 1,
			marker: {
				enabled: false,
				symbol: 'triangle-down',
				radius: 0,
			},
			states: {
				hover: {
					halo: null,
				},
			},
			data: (progress.value || []).map(() => budget.value || 0),
		},
	] as Highcharts.SeriesOptionsType[];
});

const gradientLines = computed(() => {
	return {
		gradient0: {
			tagName: 'linearGradient',
			id: 'burn-up-revenue-gradient-0',
			x1: 0,
			y1: 0,
			x2: 0,
			y2: 1,
			children: [
				{
					tagName: 'stop',
					offset: 0,
				},
				{
					tagName: 'stop',
					offset: 1,
				},
			],
		},
	} as Highcharts.DefsOptions;
});

const tooltipOptions = computed(() => {
	return tooltipShared({
		getTitle: (x) => formatDateShortNoCurrentYear(new Date(x as number)),
		getLinesToShow(pointsByName): (TooltipLine | undefined)[] {
			const format = makeTooltipFormatterTime();

			const planned = pointsByName.project_burn_up_chart_planned?.y ?? 0;

			const actual = pointsByName.project_burn_up_chart_actual?.y;

			const forecast = pointsByName.project_burn_up_chart_forecasted?.y;

			const showForecast = forecast !== undefined;

			const showActual =
				actual !== undefined && actual !== null && !showForecast;

			const getSecondaryValue = (duration: number) => {
				return budget.value && duration > budget.value
					? `+${format(duration - budget.value)}`
					: undefined;
			};

			return [
				budget.value
					? {
							label: t('project_burn_up_chart_budget'),
							value: format(budget.value),
							colorIndex: 0,
							type: 'line',
						}
					: undefined,
				{
					label: t('project_burn_up_chart_planned'),
					value: format(planned),
					secondaryValue: getSecondaryValue(planned),
					colorIndex: 1,
					type: 'line',
				},
				showActual
					? {
							label: t('project_burn_up_chart_actual'),
							value: format(actual ?? 0),
							secondaryValue: getSecondaryValue(actual ?? 0),
							colorIndex: 2,
							type: 'line',
						}
					: undefined,
				showForecast
					? {
							label: t('project_burn_up_chart_forecasted'),
							value: format(forecast ?? 0),
							secondaryValue: getSecondaryValue(forecast ?? 0),
							colorIndex: 5,
							type: 'line',
						}
					: undefined,
			];
		},
	});
});
</script>
<style lang="scss">
.burn-up-chart-time {
	&__chart {
		--highcharts-color-0: #{$teal-60};
		--highcharts-color-1: #{$blue-50};
		--highcharts-color-2: #{$blue-50};
		--highcharts-color-3: #{$blue-50};
		--highcharts-color-4: #{$blue-30};
		--highcharts-color-5: #{$blue-50};
		--highcharts-plot-line-stroke: var(--highcharts-color-0);
		--highcharts-plot-line-stroke-width: 1px;
		--highcharts-plot-line-label-color: var(--highcharts-color-0);
		--highcharts-legend-item-cursor: default;

		.highcharts-markers .highcharts-point {
			fill: $neutral-00;
			stroke-width: 2.5;
			stroke-dasharray: none;
		}

		.highcharts-series-0 {
			--highcharts-graph-stroke-width: 1px;
		}

		.highcharts-legend .highcharts-color-2 {
			stroke-dasharray: 2 3;
		}

		.highcharts-color-2 {
			stroke-dasharray: 6 8;
		}

		.highcharts-shared-tooltip__label .highcharts-color-5 {
			stroke-dasharray: 1 2;
		}

		.highcharts-color-3 {
			stroke: none;
		}

		.highcharts-color-3 .highcharts-point {
			stroke-width: 0;
			fill: var(--highcharts-color-3);
		}

		.burn-up-chart__over-budget--planned,
		.highcharts-point.burn-up-chart__over-budget--planned {
			stroke: $red-20;
		}

		.burn-up-chart__over-budget--actual,
		.highcharts-point.burn-up-chart__over-budget--actual {
			stroke: $red-50;
		}

		.highcharts-shared-tooltip__secondary-value {
			color: $red-50;
		}

		.burn-up-chart__today-line {
			stroke: $brand-20;
		}

		.burn-up-chart__today-line-text {
			font-size: 1.375rem;
			line-height: 2rem;
			color: $brand-40;
			background-color: $neutral-00;
			padding-bottom: 0.5rem;
			text-transform: uppercase;
		}

		#burn-up-revenue-gradient-0 stop {
			stop-color: $teal-10;
		}

		#burn-up-revenue-gradient-0 stop[offset='0'] {
			stop-opacity: 1;
		}

		#burn-up-revenue-gradient-0 stop[offset='1'] {
			stop-opacity: 0;
		}

		.project_burn_up_budget_shadow {
			stroke: rgb(255 0 0 / 0%);
			fill-opacity: 1;
			fill: url(#burn-up-revenue-gradient-0);
		}

		.budget-line-time {
			stroke: none;
		}
	}
}
</style>
